import React from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { NumberFormatCustom } from "../../../../../features/bonds/shared/formHelpers";
import { DATA_CORRECTION_FORM_CONFIG } from "./dataCorrectionFormConfig";

function CountyField({ counties, loadingCounties, value, onChange }) {
  return (
    <Autocomplete
      options={counties}
      value={value || ""}
      onChange={(event, newValue) => onChange(newValue)}
      loading={loadingCounties}
      renderInput={(params) => (
        <TextField
          {...params}
          label="County"
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCounties ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

CountyField.propTypes = {
  counties: PropTypes.arrayOf(PropTypes.string).isRequired,
  loadingCounties: PropTypes.bool.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

function AmountEndorsementFileField({ dataCorrectionForm, setDataCorrectionForm }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Court order document (required for bond amount Increase/Decrease)
      </Typography>
      <input
        type="file"
        accept=".pdf,.doc,.docx,image/*"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          setDataCorrectionForm((prev) => ({
            ...prev,
            amountEndorsementFile: file,
          }));
        }}
      />
      {dataCorrectionForm.amountEndorsementFile ? (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Selected: {dataCorrectionForm.amountEndorsementFile.name}
        </Typography>
      ) : (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          Required when bond amount changes
        </Typography>
      )}
    </Box>
  );
}

AmountEndorsementFileField.propTypes = {
  dataCorrectionForm: PropTypes.object.isRequired,
  setDataCorrectionForm: PropTypes.func.isRequired,
};

function CommonBondFields({
  dataCorrectionForm,
  setDataCorrectionForm,
  handleBondAmountChange,
  useNumberFormat,
  originalBondAmount,
}) {
  const showAmountEndorseFile =
    originalBondAmount != null &&
    Number(dataCorrectionForm.bondAmount || 0) !== Number(originalBondAmount || 0);
  const amountSlotProps = useNumberFormat
    ? { input: { inputComponent: NumberFormatCustom } }
    : undefined;
  const premiumSlotProps = useNumberFormat
    ? { input: { inputComponent: NumberFormatCustom, readOnly: true } }
    : undefined;

  return (
    <>
      <TextField
        label="Court Case Number"
        value={dataCorrectionForm.courtCaseNumber}
        onChange={(e) =>
          setDataCorrectionForm((prev) => ({
            ...prev,
            courtCaseNumber: e.target.value,
          }))
        }
        fullWidth
      />
      <TextField
        label="Bond Amount"
        value={dataCorrectionForm.bondAmount}
        onChange={handleBondAmountChange}
        slotProps={amountSlotProps}
        fullWidth
      />
      <TextField
        label="Premium"
        value={dataCorrectionForm.premium}
        disabled
        slotProps={premiumSlotProps}
        fullWidth
      />
      {showAmountEndorseFile && (
        <AmountEndorsementFileField
          dataCorrectionForm={dataCorrectionForm}
          setDataCorrectionForm={setDataCorrectionForm}
        />
      )}
    </>
  );
}

CommonBondFields.propTypes = {
  dataCorrectionForm: PropTypes.object.isRequired,
  setDataCorrectionForm: PropTypes.func.isRequired,
  handleBondAmountChange: PropTypes.func.isRequired,
  useNumberFormat: PropTypes.bool,
  originalBondAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function ReasonField({ dataCorrectionForm, setDataCorrectionForm }) {
  return (
    <>
      <Typography variant="h6" sx={{ mt: 2 }}>
        Reason for Change
      </Typography>
      <TextField
        label="Reason for Change"
        value={dataCorrectionForm.reason}
        onChange={(e) =>
          setDataCorrectionForm((prev) => ({ ...prev, reason: e.target.value }))
        }
        fullWidth
        multiline
        rows={2}
        required
        error={!dataCorrectionForm.reason}
        helperText={
          !dataCorrectionForm.reason ? "Please provide a reason for the change" : ""
        }
      />
    </>
  );
}

ReasonField.propTypes = {
  dataCorrectionForm: PropTypes.object.isRequired,
  setDataCorrectionForm: PropTypes.func.isRequired,
};

function ListNameFields({
  title,
  items,
  nameKey,
  labelPrefix,
  onUpdateItem,
}) {
  return (
    <>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {title}
      </Typography>
      {items.map((item, index) => (
        <Box
          key={item.id ?? `dc-item-${index}`}
          sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
        >
          <TextField
            label={`${labelPrefix} ${index + 1}'s Name`}
            value={item[nameKey] || ""}
            onChange={(e) => onUpdateItem(index, e.target.value)}
            fullWidth
          />
        </Box>
      ))}
    </>
  );
}

ListNameFields.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  nameKey: PropTypes.string.isRequired,
  labelPrefix: PropTypes.string.isRequired,
  onUpdateItem: PropTypes.func.isRequired,
};

function renderSection(section, dataCorrectionForm, setDataCorrectionForm) {
  if (section.type === "heading") {
    return (
      <Typography key={section.title} variant="h6" sx={{ mt: 2 }}>
        {section.title}
      </Typography>
    );
  }

  if (section.type === "flatFields") {
    return section.fields.map((field) => (
      <TextField
        key={field.key}
        label={field.label}
        value={dataCorrectionForm[field.key] ?? ""}
        onChange={(e) =>
          setDataCorrectionForm((prev) => ({
            ...prev,
            [field.key]: e.target.value,
          }))
        }
        fullWidth
      />
    ));
  }

  if (section.type === "nestedFields") {
    const nested = dataCorrectionForm[section.path] ?? {};
    return section.fields.map((field) => (
      <TextField
        key={`${section.path}.${field.key}`}
        label={field.label}
        value={nested[field.key] ?? ""}
        onChange={(e) =>
          setDataCorrectionForm((prev) => ({
            ...prev,
            [section.path]: {
              ...prev[section.path],
              [field.key]: e.target.value,
            },
          }))
        }
        fullWidth
      />
    ));
  }

  if (section.type === "list") {
    const items = dataCorrectionForm[section.path] ?? [];
    return (
      <ListNameFields
        key={section.path}
        title={section.title}
        items={items}
        nameKey={section.nameKey}
        labelPrefix={section.labelPrefix}
        onUpdateItem={(index, value) => {
          const next = [...items];
          next[index] = { ...next[index], [section.nameKey]: value };
          setDataCorrectionForm((prev) => ({ ...prev, [section.path]: next }));
        }}
      />
    );
  }

  return null;
}

export default function BondDataCorrectionFormBase({
  kind,
  counties,
  loadingCounties,
  dataCorrectionForm,
  setDataCorrectionForm,
  handleBondAmountChange,
  originalBondAmount,
}) {
  const config = DATA_CORRECTION_FORM_CONFIG[kind];
  if (!config) return null;

  const sectionsBeforeReason = config.reasonBeforeList
    ? config.sections.filter((s) => s.type !== "list")
    : config.sections;
  const listSections = config.reasonBeforeList
    ? config.sections.filter((s) => s.type === "list")
    : [];
  const listAfterReason = config.listAfterReason;

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <CountyField
        counties={counties}
        loadingCounties={loadingCounties}
        value={dataCorrectionForm.county}
        onChange={(value) =>
          setDataCorrectionForm((prev) => ({ ...prev, county: value }))
        }
      />
      <CommonBondFields
        dataCorrectionForm={dataCorrectionForm}
        setDataCorrectionForm={setDataCorrectionForm}
        handleBondAmountChange={handleBondAmountChange}
        useNumberFormat={config.useNumberFormat}
        originalBondAmount={originalBondAmount}
      />
      {sectionsBeforeReason.map((section) =>
        renderSection(section, dataCorrectionForm, setDataCorrectionForm)
      )}
      <ReasonField
        dataCorrectionForm={dataCorrectionForm}
        setDataCorrectionForm={setDataCorrectionForm}
      />
      {listSections.map((section) =>
        renderSection(section, dataCorrectionForm, setDataCorrectionForm)
      )}
      {listAfterReason && (
        <ListNameFields
          title={listAfterReason.title}
          items={dataCorrectionForm[listAfterReason.path] ?? []}
          nameKey={listAfterReason.nameKey}
          labelPrefix={listAfterReason.labelPrefix}
          onUpdateItem={(index, value) => {
            const items = [...(dataCorrectionForm[listAfterReason.path] ?? [])];
            items[index] = { ...items[index], [listAfterReason.nameKey]: value };
            setDataCorrectionForm((prev) => ({
              ...prev,
              [listAfterReason.path]: items,
            }));
          }}
        />
      )}
    </Box>
  );
}

BondDataCorrectionFormBase.propTypes = {
  kind: PropTypes.oneOf(["va", "trustee", "probate", "guardianship"]).isRequired,
  counties: PropTypes.arrayOf(PropTypes.string).isRequired,
  loadingCounties: PropTypes.bool.isRequired,
  dataCorrectionForm: PropTypes.object.isRequired,
  setDataCorrectionForm: PropTypes.func.isRequired,
  handleBondAmountChange: PropTypes.func.isRequired,
  originalBondAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
